import { SetURLSearchParams } from "react-router-dom";
import * as D from "schemawax";
import {
    FilterError,
    FilterErrorsWithKey,
    FiltersValidators,
    SetFilterOptions,
} from "@flexidao/ui-lib/filters-context/types";
import { validateFilters } from "./utils";

/**
 * Get the filters from the URL, decode them and validate them
 *
 * @param searchParams - The URLSearchParams object
 * @param filtersDecoder - The decoder for the filters
 * @param filtersValidators - The validators for the filters (optional)
 *
 * @returns The filters and the errors encountered while decoding and validating them
 */
export const getFiltersFromUrl = <FiltersType extends Record<string, unknown>>({
    searchParams,
    filtersDecoder,
    filtersValidators,
}: {
    searchParams: URLSearchParams;
    filtersDecoder: D.Decoder<Partial<FiltersType>>;
    filtersValidators?: FiltersValidators<FiltersType>;
}): {
    data: Partial<FiltersType> | null;
    urlFilterErrors: Array<FilterErrorsWithKey<FiltersType>>;
} => {
    const filtersFromUrl = Object.fromEntries(
        [...searchParams.entries()].map(([k, v]) => {
            if (v === "true") {
                return [k, true];
            }
            if (v === "false") {
                return [k, false];
            }

            const numValue = Number(v);
            if (!isNaN(numValue)) {
                return [k, numValue];
            }

            if (v.includes(",")) {
                return [k, v.split(",")];
            }

            return [k, v];
        }),
    );

    const decodedFiltersResult = filtersDecoder.validate(filtersFromUrl);

    if (decodedFiltersResult.type === "ok") {
        // Validate the filters from the URL
        const urlFilterErrors: Array<{
            key: keyof FiltersType | "DECODE_ERROR";
            errors: Array<FilterError>;
        }> = validateFilters({
            filters: decodedFiltersResult.data,
            filtersValidators,
        }).filter(({ errors }) => errors.length > 0);

        // Get the keys with errors
        const keysWithErrors: Array<keyof FiltersType> = urlFilterErrors
            .map(({ key }) => key)
            .filter((key) => key !== "DECODE_ERROR");

        // Remove the keys with errors from the filters object, as they are invalid
        const filtersWithoutErrors = { ...decodedFiltersResult.data };
        keysWithErrors.forEach((key) => {
            delete filtersWithoutErrors[key];
        });

        return {
            data: filtersWithoutErrors,
            urlFilterErrors,
        };
    } else {
        return {
            data: null,
            urlFilterErrors: [
                {
                    key: "DECODE_ERROR",
                    errors: [decodedFiltersResult.error.message],
                },
            ],
        };
    }
};

/**
 * Set the filters in the URL
 *
 * @param setSearchParams - The function to set the search params
 * @param filters - The filters to set
 * @param options - The options to set the search params (optional)
 *
 * @returns void
 */
export const setFiltersInUrl = <FiltersType extends Record<string, unknown>>({
    setSearchParams,
    filters,
    options,
}: {
    filters: FiltersType;
    setSearchParams: SetURLSearchParams;
    options?: SetFilterOptions;
}): void => {
    // Update the search params
    setSearchParams(parseFiltersToUrlParams({ filters }), options);
};

/**
 * Parse the filters to URL params
 *
 * @param filters - The filters to parse
 *
 * @returns The URL params
 */
export const parseFiltersToUrlParams = <FiltersType extends Record<string, unknown>>({
    filters,
}: {
    filters: FiltersType;
}): Record<string, string> => {
    // Convert params to remove nulls and convert arrays to comma-separated strings
    const urlParams: Record<string, string> = {};

    Object.keys(filters).forEach((k) => {
        const val = filters[k as keyof FiltersType];
        if (val !== null && val !== undefined) {
            urlParams[k] = Array.isArray(val) ? val.join(",") : String(val);
        }
    });

    return urlParams;
};
